import { useState, useMemo, useEffect, useCallback } from "react";
import { useNavigate, useOutletContext, useLocation } from "react-router-dom";
import { getAllDraftSplitTests } from "@/components/experiments/splitTestDraft";
import {
  PageShell,
  InlineNotice,
  EmptyState,
  SegmentedControl,
} from "@/components/shared/layout";
import DemoSiteBanner from "@/components/shared/DemoSiteBanner";
import { FeatureGate } from "@/components/shared/FeatureGate";
import { ExperimentBacklog } from "@/components/experiments/ExperimentBacklog";
import {
  ExperimentDecisionTable,
  buildDecisionRows,
  type DecisionKind,
} from "@/components/experiments/ExperimentDecisionTable";
import { CaptureIdeaDialog } from "@/components/experiments/CaptureIdeaDialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  useExperiments,
  useStopExperiment,
  useArchiveExperiment,
  type Experiment,
} from "@/hooks/useExperiments";
import { generateSlugId } from "@/lib/routing/slug-id";
import { cn } from "@/lib/utils";
import { toast } from "sonner";
import AnalyticsTabBar from "@/components/dashboard/AnalyticsTabBar";
import { HugeiconsIcon } from "@hugeicons/react"
import { Add01Icon, FileAttachmentIcon, Search01Icon } from "@hugeicons/core-free-icons"
import { useWorkspaceSafe } from "@/lib/contexts/WorkspaceContext"
import { usePlanUsage } from "@/hooks/usePlanUsage"
import { PlanUsageMeter, PlanLimitBanner, CreateOrUpgradeButton, buildPlanLimitCopy } from "@/components/shared/plan-usage"
import { getPlanDisplayName } from "@/lib/constants/pricing"
import { useSubscription } from "@/lib/contexts/SubscriptionContext"
import type { ExperimentIdea } from "@/lib/utils/experimentIdeas"
import { listExperimentIdeas, removeExperimentIdea } from "@/lib/utils/experimentIdeas"
import { getExperimentLog } from "@/lib/utils/experimentLog"
import { ShellHeaderActions } from "@/components/layout/ShellHeader";
import { ExperimentsHeader, ExperimentsListLoading, ExperimentsNoTests } from "@/components/experiments/ExperimentsListStates";
import { buildBusiestPage, cleanDomain, noTestsSubtitle } from "@/components/experiments/experimentsListModel";
import { formatRangeLabel } from "@/components/funnels/funnelListModel";
import { getPeriodUniqueVisitorTotal } from "@/lib/calculations/breakdownTotals";
import { resolvePeriodWindow } from "@/lib/utils/periodWindow";
import { useURLState } from "@/hooks/useURLState";

interface DashboardContext {
  propertyId?: string;
  property?: any;
  dashboardData?: any;
  isLoading?: boolean;
  isGuestModeOn?: boolean;
  subscription?: any;
  token?: string;
}

type StatusFilter = "all" | DecisionKind;

const STATUS_OPTIONS: { value: StatusFilter; label: string }[] = [
  { value: "all", label: "All tests" },
  { value: "decision", label: "Needs decision" },
  { value: "running", label: "Running" },
  { value: "planned", label: "Planned" },
  { value: "idea", label: "Ideas" },
  { value: "done", label: "Done" },
];

const pluralize = (n: number, one: string, many = `${one}s`) => (n === 1 ? one : many);

export default function SplitTestingPage() {
  const ctx = useOutletContext<DashboardContext>();
  const workspaceContext = useWorkspaceSafe();
  const propertyId = ctx.propertyId || "";
  const isGuestModeOn = ctx.isGuestModeOn;
  const navigate = useNavigate();
  const location = useLocation();
  const { state: urlState } = useURLState();

  const isAggregateMode = !!workspaceContext?.isAggregateMode;
  const isShareMode = location.pathname.startsWith('/s/');

  const { data, isLoading, error, refetch } = useExperiments({ propertyId, token: ctx.token });
  const stopMutation = useStopExperiment({ propertyId });
  const archiveMutation = useArchiveExperiment({ propertyId });

  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("all");
  const [viewMode, setViewMode] = useState<"board" | "list">("list");
  const [captureOpen, setCaptureOpen] = useState(false);

  // Draft split tests, captured ideas and the decision log live in localStorage.
  const [draftExperiments, setDraftExperiments] = useState<ReturnType<typeof getAllDraftSplitTests>>([]);
  const [ideas, setIdeas] = useState<ExperimentIdea[]>([]);
  const [log, setLog] = useState(() => getExperimentLog(propertyId));

  const reloadIdeas = useCallback(() => {
    setIdeas(isShareMode ? [] : listExperimentIdeas(propertyId));
  }, [propertyId, isShareMode]);

  useEffect(() => {
    const allDrafts = getAllDraftSplitTests();
    setDraftExperiments(isShareMode ? [] : allDrafts.filter(d => d.propertyId === propertyId));
    setLog(getExperimentLog(propertyId));
    reloadIdeas();
  }, [propertyId, isShareMode, reloadIdeas, viewMode]);

  const handleCreateExperiment = () => {
    navigate(`new${location.search}`, { relative: "route" });
  };

  const openExperiment = (experiment: Experiment) => {
    const slugId = generateSlugId(experiment.experimentName, experiment.experimentId);
    const basePath = location.pathname.replace(/\/split-testing.*$/, "");
    navigate(`${basePath}/split-tests/${slugId}${location.search}`);
  };

  const continueDraft = (draft: { draftId: string; currentStep?: number; testType?: string; componentPageUrl?: string }) => {
    // Element tests are built and launched in the editor (Paper "02 Editor").
    if (draft.testType === "element" && draft.componentPageUrl) {
      const basePath = location.pathname.replace(/\/split-testing.*$/, "");
      const params = new URLSearchParams(location.search);
      params.set("pageUrl", draft.componentPageUrl);
      navigate(`${basePath}/experiments/editor/${draft.draftId}?${params.toString()}`);
      return;
    }
    const stepNames = ["setup", "variants", "review"] as const;
    const stepIndex = Math.min(Math.max(draft.currentStep ?? 0, 0), stepNames.length - 1);
    navigate(`new/${draft.draftId}/${stepNames[stepIndex]}${location.search}`);
  };

  const deleteDraft = (draftId: string) => {
    localStorage.removeItem(`humblytics_draft_split_test_${draftId}`);
    setDraftExperiments((prev) => prev.filter((d) => d.draftId !== draftId));
    toast.success("Draft deleted");
  };

  const createFromIdea = (idea: ExperimentIdea) => {
    const params = new URLSearchParams(location.search);
    if (idea.pageUrl) params.set("page", idea.pageUrl);
    params.set("goal", "form_submissions");
    removeExperimentIdea(idea.id);
    navigate(`new?${params.toString()}`, { relative: "route" });
  };

  const deleteIdea = (idea: ExperimentIdea) => {
    removeExperimentIdea(idea.id);
    reloadIdeas();
    toast.success("Idea deleted");
  };

  const experiments: Experiment[] = useMemo(() => data ?? [], [data]);

  const rows = useMemo(
    () => buildDecisionRows({ experiments, drafts: draftExperiments, ideas, log }),
    [experiments, draftExperiments, ideas, log]
  );

  const statusCounts = useMemo(() => {
    const counts: Record<StatusFilter, number> = {
      all: rows.length, decision: 0, running: 0, planned: 0, idea: 0, done: 0,
    };
    rows.forEach((r) => counts[r.kind]++);
    return counts;
  }, [rows]);

  const filteredRows = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    return rows.filter((r) => {
      if (statusFilter !== "all" && r.kind !== statusFilter) return false;
      if (!query) return true;
      return (
        r.name.toLowerCase().includes(query) ||
        r.meta.toLowerCase().includes(query) ||
        r.goal.toLowerCase().includes(query) ||
        (Array.isArray(r.experiment?.variants) ? r.experiment!.variants : []).some((v) =>
          v.url?.toLowerCase().includes(query)
        )
      );
    });
  }, [rows, statusFilter, searchQuery]);

  // Stat strip: running tests, lift across decided tests, visitors in tests.
  const stats = useMemo(() => {
    const running = experiments.filter((e) => e.status === "active");
    const decided = rows.filter((r) => r.experiment && r.experiment.status !== "active");
    const winners = decided.filter((r) => r.winLift != null);
    const avgLift = winners.length
      ? winners.reduce((s, r) => s + (r.winLift ?? 0), 0) / winners.length
      : null;
    const sessionsOf = (list: Experiment[]) =>
      list.reduce(
        (sum, exp) =>
          sum + (Array.isArray(exp.variants) ? exp.variants : []).reduce((vs, v) => vs + (v.sessions || 0), 0),
        0
      );
    return {
      running: running.length,
      avgLift,
      successRate: decided.length ? Math.round((winners.length / decided.length) * 100) : null,
      winners: winners.length,
      decided: decided.length,
      visitors: running.length ? sessionsOf(running) : sessionsOf(experiments),
    };
  }, [experiments, rows]);

  const subtitle = useMemo(() => {
    const domain = isAggregateMode ? undefined : (ctx.property?.domain as string | undefined);
    const tested = experiments.length;
    const first = experiments.reduce<number | null>((min, e) => {
      const t = new Date(e.createdAt).getTime();
      return Number.isNaN(t) ? min : min == null ? t : Math.min(min, t);
    }, null);
    const applied = log.filter((l) => l.action === "ship").length;
    return [
      domain,
      tested > 0 &&
        `${tested} ${pluralize(tested, "test")}${
          first ? ` since ${new Date(first).toLocaleDateString("en-US", { month: "long" })}` : ""
        }`,
      applied > 0 && `${applied} ${pluralize(applied, "winner")} applied`,
    ]
      .filter(Boolean)
      .join(" · ");
  }, [ctx.property, experiments, isAggregateMode, log]);

  const handleStopExperiment = async (experiment: Experiment) => {
    try {
      await stopMutation.mutateAsync(experiment.experimentId);
      toast.success(`"${experiment.experimentName}" has been paused`);
      refetch();
    } catch {
      toast.error("Failed to stop experiment");
    }
  };

  const handleArchiveExperiment = async (experiment: Experiment) => {
    try {
      await archiveMutation.mutateAsync(experiment.experimentId);
      toast.success(`"${experiment.experimentName}" has been archived`);
      refetch();
    } catch {
      toast.error("Failed to archive experiment");
    }
  };

  const plannerPath = `${location.pathname.replace(/\/split-testing.*$/, '')}/test-planner`;

  // Plan-limit awareness — mirrors the heatmaps pattern. Usage invalidation
  // on create/stop/resume/archive happens directly in the mutation hooks
  // (useExperiments.ts), so this page only needs to read it. The locally
  // loaded active-test count feeds the fallback so the gate keeps working
  // when the usage endpoint can't answer.
  const activeCount = experiments.filter(e => e.status === 'active').length;
  const { splitTests: splitTestsUsage, metadata: planUsageMetadata } = usePlanUsage({
    propertyId,
    fallbackCounts: { splitTests: activeCount },
  });
  const [testLimitBannerDismissed, setTestLimitBannerDismissed] = useState(false);
  useEffect(() => {
    if (splitTestsUsage.canAdd && testLimitBannerDismissed) setTestLimitBannerDismissed(false);
  }, [splitTestsUsage.canAdd, testLimitBannerDismissed]);
  const { currentPlan, planName } = useSubscription();
  const displayPlanName = getPlanDisplayName(currentPlan, planUsageMetadata?.planLabel || planName);
  const splitTestLimitCopy = buildPlanLimitCopy({
    noun: "experiments",
    limit: splitTestsUsage.limit,
    planName: displayPlanName,
    resolution: "Stop or archive a test, or upgrade for more.",
  });
  const limitReached = !splitTestsUsage.canAdd;
  // Structural reasons a create action can't happen (guest mode / missing property) are
  // distinct from a plan limit — only the latter should swap the CTA to "Upgrade".
  const createBlocked = isGuestModeOn || !propertyId;
  const canCreate = !createBlocked && !limitReached;

  // Rendered into the shell header's actions slot (Paper: "New test" top right).
  const primaryActions = !isShareMode && (
    <ShellHeaderActions>
      <Button variant="outline" size="sm" className="h-8" onClick={() => navigate(plannerPath)}>
        Plan a test
      </Button>
      <CreateOrUpgradeButton
        size="sm"
        className="h-8 gap-1.5"
        atLimit={limitReached}
        onClick={handleCreateExperiment}
        disabled={createBlocked}
        noun="experiments"
        limit={splitTestsUsage.limit}
        planName={displayPlanName}
      >
        <HugeiconsIcon icon={Add01Icon} size={12} />
        New test
      </CreateOrUpgradeButton>
    </ShellHeaderActions>
  );

  if (isLoading || ctx.isLoading) {
    return (
      <PageShell>
        {primaryActions}
        <ExperimentsListLoading />
      </PageShell>
    );
  }

  if (error) {
    return (
      <PageShell>
        <ExperimentsHeader subtitle="Create and manage A/B tests" actions={primaryActions} />
        <InlineNotice
          variant="error"
          message="Failed to load experiments"
          description={
            error instanceof Error
              ? error.message
              : "An error occurred while loading experiments. Please try again."
          }
        />
        <div className="flex justify-center mt-4">
          <Button onClick={() => refetch()} variant="outline">
            Retry
          </Button>
        </div>
      </PageShell>
    );
  }

  const shareBasePath = isShareMode ? location.pathname.replace(/\/split-testing.*$/, '') : '';
  const hasAnything = experiments.length > 0 || draftExperiments.length > 0 || ideas.length > 0;
  // Paper "Experiments · No tests yet": no stat strip or filters, the empty
  // table points at the busiest page and the planner.
  const noTests = !hasAnything && !isShareMode;
  const siteDomain = isAggregateMode ? "" : cleanDomain(ctx.property?.domain);
  const periodVisitors = getPeriodUniqueVisitorTotal(ctx.dashboardData?.metrics);
  const periodRange = (() => {
    const w = resolvePeriodWindow(urlState.period || "7d");
    return w ? formatRangeLabel(w.start, w.end) : null;
  })();
  const busiestPage = noTests
    ? buildBusiestPage({
        pages: ctx.dashboardData?.metrics?.pageSessions,
        totalVisitors: periodVisitors,
        domain: siteDomain,
        period: urlState.period,
      })
    : null;
  const testBusiestPage = busiestPage && canCreate
    ? () => {
        const params = new URLSearchParams(location.search);
        params.set("page", busiestPage.path);
        navigate(`new?${params.toString()}`, { relative: "route" });
      }
    : undefined;

  return (
    <FeatureGate feature="splitTesting">
      <PageShell className="space-y-6">
        {isShareMode && <AnalyticsTabBar basePath={shareBasePath} />}

        <ExperimentsHeader
          title={noTests && siteDomain ? `No tests on ${siteDomain} yet.` : undefined}
          subtitle={
            noTests
              ? noTestsSubtitle({ domain: siteDomain, visitors: periodVisitors, period: urlState.period, range: periodRange }) ||
                "Create and manage A/B tests"
              : subtitle || "Create and manage A/B tests"
          }
          actions={
            <>
              {activeCount > 0 && !isShareMode && (
                <PlanUsageMeter label="Active tests" counter={splitTestsUsage} />
              )}
              {primaryActions}
            </>
          }
        />

        {/* Plan-limit banner, same pattern as heatmaps and funnels. */}
        {limitReached && !isShareMode && !testLimitBannerDismissed && (
          <PlanLimitBanner
            title={splitTestLimitCopy.title}
            description={splitTestLimitCopy.description}
            noun="experiments"
            planName={displayPlanName}
            onDismiss={() => setTestLimitBannerDismissed(true)}
            isOwner={planUsageMetadata?.isOwner ?? true}
          />
        )}

        <DemoSiteBanner propertyId={propertyId} />

        {/* View + filters */}
        {!noTests && (
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
          {!isShareMode ? (
            <SegmentedControl
              options={[
                { value: "list", label: "Table" },
                { value: "board", label: "Board" },
              ]}
              value={viewMode}
              onChange={setViewMode}
            />
          ) : (
            <span />
          )}
          {(viewMode === "list" || isShareMode) && (
            <div className="flex items-center gap-2">
              <Select value={statusFilter} onValueChange={(v) => setStatusFilter(v as StatusFilter)}>
                <SelectTrigger size="sm" className="h-8 w-[168px] text-xs" aria-label="Filter by status">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {STATUS_OPTIONS.map((opt) => (
                    <SelectItem key={opt.value} value={opt.value} className="text-xs">
                      {opt.label}
                      <span className="ml-1 tabular-nums text-muted-foreground">{statusCounts[opt.value]}</span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <div className="relative w-full sm:w-56">
                <HugeiconsIcon icon={Search01Icon} size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <Input
                  placeholder="Search tests"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-8 h-8 text-xs data-search-input"
                />
              </div>
            </div>
          )}
        </div>
        )}

        {viewMode === "board" && !isShareMode && !noTests ? (
          <div className="well">
            <ExperimentBacklog
              propertyId={propertyId}
              experiments={experiments}
              drafts={draftExperiments}
              onOpenExperiment={openExperiment}
              onContinueDraft={continueDraft}
              onDeleteDraft={deleteDraft}
              onCreateFromIdea={createFromIdea}
              onNewExperiment={handleCreateExperiment}
            />
          </div>
        ) : (
          <div className="well flex flex-col gap-2">
            {/* Stat strip */}
            {!noTests && (
            <div className="surface-raised flex flex-wrap items-center gap-x-6 gap-y-3 px-5 py-4 sm:gap-x-0">
              <Stat value={String(stats.running)} label={pluralize(stats.running, "running test")} className="sm:pr-7" />
              <StatDivider />
              {/* Winners out of finished tests first; the average lift only once
                  there is a winner to average ("–" next to "0 of 21 decided" read
                  as nothing being finished). */}
              <Stat
                value={stats.decided === 0 ? "–" : `${stats.winners} of ${stats.decided}`}
                label={
                  stats.decided === 0
                    ? "finished tests so far"
                    : stats.avgLift == null || stats.winners === 0
                      ? "finished tests found a winner"
                      : `finished tests found a winner · average lift ${stats.avgLift < 0 ? "\u2212" : "+"}${Math.round(Math.abs(stats.avgLift))}%`
                }
                className="sm:px-7"
              />
              <StatDivider />
              <Stat
                value={stats.visitors.toLocaleString("en-US")}
                label={stats.running > 0 ? "visitors in running tests" : "visitors in tests so far"}
                className="sm:pl-7"
              />
            </div>
            )}

            <ExperimentDecisionTable
              rows={filteredRows}
              readOnly={isShareMode}
              onOpenExperiment={openExperiment}
              onContinueDraft={continueDraft}
              onDeleteDraft={deleteDraft}
              onPlanIdea={createFromIdea}
              onDeleteIdea={deleteIdea}
              onPauseExperiment={handleStopExperiment}
              onArchiveExperiment={handleArchiveExperiment}
              onCaptureIdea={() => setCaptureOpen(true)}
              hideFooter={noTests}
              empty={
                noTests ? (
                  <ExperimentsNoTests
                    busiestPage={busiestPage}
                    onTestPage={testBusiestPage}
                    onOpenPlanner={() => navigate(plannerPath)}
                  />
                ) : (
                <EmptyState
                  title={hasAnything ? "No matching tests" : "No experiments yet"}
                  description={
                    hasAnything
                      ? "Try another status or search."
                      : "Create your first A/B test to start optimizing conversions on your site."
                  }
                  action={
                    !isShareMode && !hasAnything && canCreate ? (
                      <Button variant="outline" onClick={handleCreateExperiment}>Create first test</Button>
                    ) : undefined
                  }
                  secondaryAction={
                    !hasAnything ? (
                      <Button variant="outline" asChild>
                        <a href="https://docs.humblytics.com" target="_blank" rel="noopener noreferrer">
                          <HugeiconsIcon icon={FileAttachmentIcon} size={14} className="mr-1.5" />
                          How A/B testing works
                        </a>
                      </Button>
                    ) : undefined
                  }
                />
                )
              }
            />
          </div>
        )}

        {!isShareMode && (
          <CaptureIdeaDialog
            open={captureOpen}
            onOpenChange={setCaptureOpen}
            propertyId={propertyId}
            onCaptured={() => {
              reloadIdeas();
              toast.success("Idea captured");
            }}
          />
        )}
      </PageShell>
    </FeatureGate>
  );
}

function Stat({ value, label, className }: { value: string; label: string; className?: string }) {
  return (
    <div className={cn("flex items-baseline gap-2", className)}>
      <span className="text-[22px] font-semibold leading-7 tabular-nums text-foreground">{value}</span>
      <span className="text-[13px] leading-4 text-muted-foreground">{label}</span>
    </div>
  );
}

function StatDivider() {
  return <span aria-hidden className="hidden h-7 w-px shrink-0 bg-foreground/[0.08] sm:block" />;
}
